import { API_URL } from '../constants';
import store from '../store';
import { logOutUser } from '../store/thunk';
import { getFreshAccessToken } from './auth';

/**
 * Designer review packs ("batches") of print-ready files. All endpoints need the Cognito JWT.
 *
 *  POST /aav/print-files/batches             — create ONE pack (~10 files); { result: null } when nothing is eligible
 *  POST /aav/print-files/batches/{id}        — set status to in_progress | done
 *  GET  /aav/print-files/batches             — list packs with review progress
 *  POST /aav/print-files/{printReadyFileId}  — save the design review (printFileStatus → reviewed)
 *  POST /aav/print-files/upload-url          — presigned S3 PUT for a manually edited final file
 *  GET  /aav/print-files/batches/{id}        — one batch with its files and per-file logs
 *
 * Designers review from IndexedDB (filled by the create call); the GET by id is for viewing
 * packs from elsewhere and for the moderator's pack statistics.
 */

export type PrintFileBatchStatus = 'init' | 'in_progress' | 'done';
export type PrintFileReviewResult = 'ok' | 'need_design' | 'need_cs';
export type PrintFileUploadType = 'image/jpeg' | 'image/png' | 'image/webp' | 'image/tiff';

export const PRINT_FILE_BATCH_STATUSES: PrintFileBatchStatus[] = ['init', 'in_progress', 'done'];
export const PRINT_FILE_BATCHES_MAX_LIMIT = 200;

export interface IBatchPrintFile {
    printReadyFileId: string;
    orderNumber: string;
    sku: string;
    originalUrl: string | null;
    mockupUrl: string | null;
    croppingNotes: string | null;
    color: string | null;
    printFileStatus: string;
    draftPrintReadyUrl: string | null;
    backgroundRemovedUrl: string | null;
    /** AI-upscaled variant of the draft / of the background-removed file, when the pipeline made one. */
    draftPrintReadyFileUpscaledUrl?: string | null;
    backgroundRemovedFileUpscaledUrl?: string | null;
}

export interface ICreatedPrintFileBatch {
    printfileBatchId: string;
    status: PrintFileBatchStatus;
    minOrderDate: string | null;
    maxOrderDate: string | null;
    targetSize: number;
    printFiles: IBatchPrintFile[];
}

export interface IPrintFileBatchSummary {
    printfileBatchId: string;
    status: PrintFileBatchStatus;
    createdBy: string | null;
    createdAt: string;
    updatedAt: string;
    fileCount: number;
    reviewedFileCount: number;
}

export interface IPrintFileBatchesResponse {
    result: IPrintFileBatchSummary[];
    totalCount: number;
    page: number;
    perPage: number;
    /** Ignores the status filter. */
    countsByStatus: { status: PrintFileBatchStatus; count: number }[];
    /** Print files waiting for design review (not in any batch yet). */
    pendingReviewCount: number | null;
}

/** A review of a file taken from its server log (designer POST or the automatic path). */
export interface IPrintFileReviewEvent {
    at: string;
    /** Designer name (Cognito); null for the automatic path. */
    by: string | null;
    auto: boolean;
    result: string | null;
    /** Which file became final: the draft as is, or a manually edited upload. */
    finalSource: 'draft' | 'manual_edits' | null;
}

/** The parts of a file's server log the review page uses (keys and raw payloads are dropped). */
export interface IPrintFileHistory {
    /** File row created by the order sync. */
    createdAt: string | null;
    /** Last print-ready pipeline run finished. */
    processedAt: string | null;
    jobRuns: number;
    /** Status of the last pipeline run (succeeded / failed / skipped). */
    jobStatus: string | null;
    /** errorStep or skipReason of the last run. */
    jobIssue: string | null;
    /** Oldest first. */
    reviews: IPrintFileReviewEvent[];
}

export interface IBatchPrintFileDetails extends IBatchPrintFile {
    reviewResult: PrintFileReviewResult | null;
    hasManualEdits: boolean | null;
    finalPrintReadyUrl: string | null;
    history: IPrintFileHistory;
}

export interface IPrintFileBatchDetails {
    printfileBatchId: string;
    status: PrintFileBatchStatus;
    createdBy: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    fileCount: number;
    reviewedFileCount: number;
    minOrderDate: string | null;
    maxOrderDate: string | null;
    printFiles: IBatchPrintFileDetails[];
}

export interface IPrintFileBatchesQueryParams {
    page?: number;
    limit?: number;
    sortDirection?: 'ASC' | 'DESC';
    status?: PrintFileBatchStatus[];
}

export type IPrintFileReviewBody =
    | { reviewResult: PrintFileReviewResult; hasManualEdits: false }
    | { reviewResult: PrintFileReviewResult; hasManualEdits: true; finalPrintReadyUrl: string };

export interface IReviewedPrintFile {
    printReadyFileId: string;
    orderId: string;
    sku: string;
    originalUrl: string | null;
    mockupUrl: string | null;
    croppingNotes: string | null;
    color: string | null;
    printFileStatus: string;
    reviewResult: PrintFileReviewResult;
    hasManualEdits: boolean;
    draftPrintReadyUrl: string | null;
    finalPrintReadyUrl: string | null;
    printfileBatchId: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface IPrintFileUploadUrl {
    signedUrl: string;
    fileName: string;
    key: string;
    /** CDN URL to pass as finalPrintReadyUrl. */
    url: string;
}

/** Errors keep the HTTP status so the sync queue can tell bad requests from transient failures. */
export type PrintFileApiError = Error & { status?: number };

const apiError = (message: string, status?: number): PrintFileApiError =>
    Object.assign(new Error(message), { status });

/**
 * The review page stays open for hours and syncs in the background; the access token kept
 * in the store expires after an hour, so ask Amplify for a (transparently refreshed) one.
 */
const getAuthHeaders = async () => {
    let token = store.getState().auth.data.token;
    try {
        token = await getFreshAccessToken();
    } catch (e) {
        /* no Amplify session — fall back to the stored token */
    }
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${encodeURIComponent(token)}`,
    };
};

const handleResponse = async (res: Response) => {
    if (res.ok) return await res.json();
    if (res.status === 401) {
        store.dispatch(logOutUser());
        throw apiError('Unauthorized!', 401);
    }
    let message = res.status === 400 ? 'Invalid request' : res.status === 404 ? 'Not found' : `Server error (${res.status})`;
    try {
        const body = await res.json();
        if (body?.message) message = body.message;
    } catch (e) {
        /* ignore */
    }
    throw apiError(message, res.status);
};

export const createPrintFileBatch = async (): Promise<ICreatedPrintFileBatch | null> => {
    const res = await fetch(`${API_URL}/aav/print-files/batches`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({}),
    });
    const data = await handleResponse(res);
    const result = data?.result;
    if (!result) return null;
    return {
        ...result,
        printfileBatchId: String(result.printfileBatchId),
        printFiles: Array.isArray(result.printFiles)
            ? result.printFiles.map((f: IBatchPrintFile) => ({ ...f, printReadyFileId: String(f.printReadyFileId) }))
            : [],
    };
};

export const setPrintFileBatchStatus = async (
    printfileBatchId: string,
    status: Exclude<PrintFileBatchStatus, 'init'>
): Promise<{ printfileBatchId: string; status: PrintFileBatchStatus; createdAt: string }> => {
    const res = await fetch(`${API_URL}/aav/print-files/batches/${encodeURIComponent(printfileBatchId)}`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ status }),
    });
    const data = await handleResponse(res);
    return data?.result;
};

export const stringifyPrintFileBatchesParameters = (params: IPrintFileBatchesQueryParams): string => {
    const query: string[] = [];
    if (params.page) query.push(`page=${Math.max(1, Math.floor(params.page))}`);
    if (params.limit) {
        query.push(`limit=${Math.min(PRINT_FILE_BATCHES_MAX_LIMIT, Math.max(1, Math.floor(params.limit)))}`);
    }
    if (params.sortDirection === 'ASC' || params.sortDirection === 'DESC') query.push(`sortDirection=${params.sortDirection}`);
    if (params.status?.length) query.push(`status=${params.status.join(',')}`);
    return query.join('&');
};

export const getPrintFileBatches = async (params: IPrintFileBatchesQueryParams): Promise<IPrintFileBatchesResponse> => {
    const res = await fetch(`${API_URL}/aav/print-files/batches?${stringifyPrintFileBatchesParameters(params)}`, {
        method: 'GET',
        headers: await getAuthHeaders(),
    });
    const data = await handleResponse(res);
    return {
        result: Array.isArray(data?.result)
            ? data.result.map((b: IPrintFileBatchSummary) => ({ ...b, printfileBatchId: String(b.printfileBatchId) }))
            : [],
        totalCount: Number(data?.totalCount ?? 0),
        page: Number(data?.page ?? params.page ?? 1),
        perPage: Number(data?.perPage ?? params.limit ?? 50),
        countsByStatus: Array.isArray(data?.countsByStatus) ? data.countsByStatus : [],
        pendingReviewCount: data?.pendingReviewCount != null ? Number(data.pendingReviewCount) : null,
    };
};

const summarizeFileLogs = (logs: any[]): IPrintFileHistory => {
    const history: IPrintFileHistory = { createdAt: null, processedAt: null, jobRuns: 0, jobStatus: null, jobIssue: null, reviews: [] };
    (Array.isArray(logs) ? logs : []).forEach((log) => {
        const after = log?.afterData || {};
        switch (log?.type) {
            case 'created':
                history.createdAt = history.createdAt ?? log.createdAt ?? null;
                break;
            case 'jobCompleted':
                history.jobRuns += 1;
                history.processedAt = log.createdAt ?? history.processedAt;
                history.jobStatus = after.jobStatus ?? null;
                history.jobIssue = after.errorStep || after.skipReason || null;
                break;
            case 'reviewCompleted':
            case 'designReviewed':
                history.reviews.push({
                    at: log.createdAt,
                    by: log.type === 'designReviewed' ? log.sourceLabel ?? null : null,
                    auto: log.type === 'reviewCompleted',
                    result: after.reviewResult ?? null,
                    finalSource: after.finalSource === 'draft' || after.finalSource === 'manual_edits' ? after.finalSource : null,
                });
                break;
            default:
                break;
        }
    });
    return history;
};

/** One batch with its files; logs are reduced to IPrintFileHistory, everything else unused is dropped. */
export const getPrintFileBatch = async (printfileBatchId: string): Promise<IPrintFileBatchDetails> => {
    const res = await fetch(`${API_URL}/aav/print-files/batches/${encodeURIComponent(printfileBatchId)}`, {
        method: 'GET',
        headers: await getAuthHeaders(),
    });
    const data = await handleResponse(res);
    const r = data?.result;
    if (!r || !Array.isArray(r.printFiles)) throw apiError('Set files missing in response', res.status);
    return {
        printfileBatchId: String(r.printfileBatchId ?? printfileBatchId),
        status: r.status,
        createdBy: r.createdBy ?? null,
        createdAt: r.createdAt ?? null,
        updatedAt: r.updatedAt ?? null,
        fileCount: Number(r.fileCount ?? r.printFiles.length),
        reviewedFileCount: Number(r.reviewedFileCount ?? 0),
        minOrderDate: r.minOrderDate ?? null,
        maxOrderDate: r.maxOrderDate ?? null,
        printFiles: r.printFiles.map((f: any) => ({
            printReadyFileId: String(f.printReadyFileId),
            orderNumber: f.orderNumber ?? '',
            sku: f.sku ?? '',
            originalUrl: f.originalUrl ?? null,
            mockupUrl: f.mockupUrl ?? null,
            croppingNotes: f.croppingNotes ?? null,
            color: f.color ?? null,
            printFileStatus: f.printFileStatus ?? '',
            // The current job's output stands in when no draft was promoted yet.
            draftPrintReadyUrl: f.draftPrintReadyUrl ?? f.preprocessedPrintReadyUrl ?? null,
            backgroundRemovedUrl: f.backgroundRemovedUrl ?? null,
            draftPrintReadyFileUpscaledUrl: f.draftPrintReadyFileUpscaledUrl ?? null,
            backgroundRemovedFileUpscaledUrl: f.backgroundRemovedFileUpscaledUrl ?? null,
            reviewResult: f.reviewResult ?? null,
            hasManualEdits: f.hasManualEdits ?? null,
            finalPrintReadyUrl: f.finalPrintReadyUrl ?? null,
            history: summarizeFileLogs(f.logs),
        })),
    };
};

export const savePrintFileReview = async (
    printReadyFileId: string,
    body: IPrintFileReviewBody
): Promise<IReviewedPrintFile | null> => {
    const res = await fetch(`${API_URL}/aav/print-files/${encodeURIComponent(printReadyFileId)}`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(body),
    });
    const data = await handleResponse(res);
    return data?.result ?? null;
};

export const getPrintFileUploadUrl = async (
    type: PrintFileUploadType,
    printReadyFileId?: string
): Promise<IPrintFileUploadUrl> => {
    const res = await fetch(`${API_URL}/aav/print-files/upload-url`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(printReadyFileId ? { type, printReadyFileId } : { type }),
    });
    const data = await handleResponse(res);
    if (!data?.result?.signedUrl || !data?.result?.url) throw apiError('Upload URL missing in response', res.status);
    return data.result;
};

/** PUT the file to the presigned URL. Content-Type must equal the type the URL was signed for. */
export const uploadPrintFileToS3 = async (signedUrl: string, blob: Blob, type: PrintFileUploadType): Promise<void> => {
    let res: Response;
    try {
        res = await fetch(signedUrl, { method: 'PUT', headers: { 'Content-Type': type }, body: blob });
    } catch (e) {
        // fetch only rejects on network/CORS failures (the bucket must allow PUT from this origin).
        throw apiError('S3 upload blocked (network or bucket CORS)', 0);
    }
    if (!res.ok) throw apiError(`S3 upload failed (${res.status})`, res.status);
};
